import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Ad {
  id: string;
  title: string;
  description: string | null;
  image_url: string;
  link_url: string | null;
}

export function AdDisplay() {
  const [ads, setAds] = useState<Ad[]>([]);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasClosedOnce, setHasClosedOnce] = useState(false);
  const [showAfterDelay, setShowAfterDelay] = useState(false);

  useEffect(() => {
    loadAds();
    
    // Show ad after 2 minutes (120 seconds)
    const delayTimer = setTimeout(() => {
      setShowAfterDelay(true);
      setIsVisible(true);
    }, 120000);

    return () => clearTimeout(delayTimer);
  }, []);

  useEffect(() => {
    if (ads.length === 0 || !showAfterDelay) return;

    // Rotate ads every 60 seconds (1 minute)
    const interval = setInterval(() => {
      setCurrentAdIndex((prev) => (prev + 1) % ads.length);
      setIsVisible(true); // Show ad again after rotation
    }, 60000);

    return () => clearInterval(interval);
  }, [ads.length, showAfterDelay]);

  const loadAds = async () => {
    try {
      const { data, error } = await supabase
        .from('ads')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      if (data && data.length > 0) {
        setAds(data);
      }
    } catch (error) {
      console.error('Error loading ads:', error);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setHasClosedOnce(true);
  };

  if (ads.length === 0 || !isVisible || !showAfterDelay) {
    return null;
  }

  const currentAd = ads[currentAdIndex];

  const AdContent = () => (
    <div className="relative bg-card border-2 border-border rounded-2xl shadow-2xl overflow-hidden max-w-2xl w-full animate-scale-in">
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-3 right-3 z-10 h-10 w-10 rounded-full bg-background hover:bg-muted transition-all hover:scale-110"
        onClick={handleClose}
      >
        <X className="h-5 w-5" />
      </Button>
      
      <div className="p-6">
        <div className="flex flex-col md:flex-row gap-6">
          <img
            src={currentAd.image_url}
            alt={currentAd.title}
            className="w-full md:w-56 h-56 object-cover rounded-xl"
          />
          <div className="flex-1">
            <h3 className="text-2xl font-bold text-foreground mb-3">
              {currentAd.title}
            </h3>
            {currentAd.description && (
              <p className="text-muted-foreground mb-6 text-base leading-relaxed">
                {currentAd.description}
              </p>
            )}
            {currentAd.link_url && (
              <a
                href={currentAd.link_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
              >
                <Button size="lg" className="hover:scale-105 transition-transform">En savoir plus</Button>
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Backdrop overlay */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] animate-fade-in"
        onClick={handleClose}
      />
      
      {/* Centered ad */}
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none">
        <div className="pointer-events-auto">
          <AdContent />
        </div>
      </div>
    </>
  );
}
